import { Dataset, DatasetItem } from '../../../types/types';
import { ReportBlock, ReportDataSource, ReportTemplate } from '../types';
import { isNumericItemType } from './isNumericItemType';
import {
  resolveTemplateDefinitionSources,
  resolveTemplateItemNames,
  resolveTemplateSources,
} from './resolveTemplateItemNames';

export type ReportTemplateReferenceIssue =
  | { kind: 'missingSource'; source: ReportDataSource }
  | { kind: 'missingItem'; source: ReportDataSource; item: string }
  | { kind: 'nonNumericItem'; source: ReportDataSource; item: string }
  | { kind: 'missingFilterItem'; source: ReportDataSource; item: string };

const definitionItems = (dataset: Dataset): DatasetItem[] =>
  Array.isArray(dataset.items) ? dataset.items : [];

const findItem = (
  definitionsBySource: Record<string, Dataset>,
  source: ReportDataSource,
  itemName: string
): DatasetItem | undefined =>
  definitionItems(definitionsBySource[source]).find(
    (item) => item.name === itemName
  );

const findBlockReferenceIssue = (
  block: ReportBlock,
  definitionsBySource: Record<string, Dataset>
): ReportTemplateReferenceIssue | undefined => {
  if (block.kind === 'row') {
    for (const cell of block.cells) {
      for (const cellBlock of cell.blocks) {
        const issue = findBlockReferenceIssue(
          cellBlock,
          definitionsBySource
        );
        if (issue) return issue;
      }
    }
    return undefined;
  }

  if (block.kind === 'aggregate' && block.operation !== 'count' && block.item) {
    const item = findItem(definitionsBySource, block.source, block.item);
    if (item && !isNumericItemType(item.type)) {
      return {
        kind: 'nonNumericItem',
        source: block.source,
        item: block.item,
      };
    }
  }

  if (block.kind === 'taxBreakdown') {
    for (const itemName of [block.groupBy, block.net, block.gross]) {
      const item = findItem(definitionsBySource, block.source, itemName);
      if (item && !isNumericItemType(item.type)) {
        return {
          kind: 'nonNumericItem',
          source: block.source,
          item: itemName,
        };
      }
    }
  }

  if (
    block.kind === 'meta' &&
    block.field === 'filterValue' &&
    block.filterSource &&
    block.filterItem
  ) {
    const items = definitionItems(definitionsBySource[block.filterSource]);
    const exists = items.some(
      (item) =>
        item.name === block.filterItem || item.filterParam === block.filterItem
    );
    if (!exists) {
      return {
        kind: 'missingFilterItem',
        source: block.filterSource,
        item: block.filterItem,
      };
    }
  }

  return undefined;
};

/**
 * Checks stored references against the definitions available at print time.
 * Structural JSON validation cannot catch a column that was later renamed or
 * removed; allowing that drift through can turn a missing sum into a plausible
 * zero on paper.
 */
export const findReportTemplateReferenceIssue = (
  template: ReportTemplate,
  definitionsBySource: Record<string, Dataset>
): ReportTemplateReferenceIssue | undefined => {
  const sources = Array.from(
    new Set([
      ...resolveTemplateSources(template),
      ...resolveTemplateDefinitionSources(template),
    ])
  );

  for (const source of sources) {
    if (!definitionsBySource[source]) {
      return { kind: 'missingSource', source };
    }
  }

  for (const source of resolveTemplateSources(template)) {
    const itemNames = new Set(
      definitionItems(definitionsBySource[source]).map((item) => item.name)
    );
    for (const requestedItem of resolveTemplateItemNames(template, source)) {
      if (!itemNames.has(requestedItem)) {
        return { kind: 'missingItem', source, item: requestedItem };
      }
    }
  }

  const { header, footer } = template.sections;
  for (const block of header?.blocks ?? []) {
    const issue = findBlockReferenceIssue(block, definitionsBySource);
    if (issue) return issue;
  }
  for (const block of footer?.blocks ?? []) {
    const issue = findBlockReferenceIssue(block, definitionsBySource);
    if (issue) return issue;
  }

  return undefined;
};

export const describeReportTemplateReferenceIssue = (
  issue: ReportTemplateReferenceIssue
): string => {
  if (issue.kind === 'missingSource') {
    return `Invalid report template source: ${issue.source}`;
  }
  return `Invalid report template ${issue.kind}: ${issue.source}.${issue.item}`;
};
